import { Phone, MessageSquare, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

const WA = "https://wa.me/919748600483?text=Hi,%20I%20need%20help%20with%20my%20animal";

const navLinks = [
  { name: "Home", href: "/" },
  { name: "Services", href: "/#services" },
  { name: "Pets", href: "/#animals" },
  { name: "Areas", href: "/#reach" },
  { name: "Guides", href: "/blog" },
  { name: "FAQ", href: "/#faq" },
  { name: "Contact", href: "/#contact" },
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-[#FAF6EF]/90 border-b border-[#E8E2D2]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center space-x-3" data-testid="site-logo-link">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm overflow-hidden border border-[#E8E2D2]">
            <img src="/logo.png" alt="Animal Analytics logo" className="w-10 h-10 object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-[#1A4D45] leading-none">
              Animal Analytics
            </span>
            <span className="text-[10px] sm:text-xs tracking-[0.15em] font-semibold text-[#C89D3C] uppercase mt-1">
              Pet Pathology Lab, Kolkata
            </span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center space-x-6">
          {navLinks.map((l) =>
            l.href.startsWith("/#") || l.href === "/" ? (
              <a key={l.name} href={l.href} className="text-sm font-medium text-[#4A4743] hover:text-[#1A4D45] hover:underline underline-offset-4 transition-colors">
                {l.name}
              </a>
            ) : (
              <Link key={l.name} to={l.href} className="text-sm font-medium text-[#4A4743] hover:text-[#1A4D45] hover:underline underline-offset-4 transition-colors">
                {l.name}
              </Link>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center space-x-3 shrink-0">
          <a href={WA} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#C89D3C] text-white hover:bg-[#b08732] transition-all">
            <MessageSquare size={14} /> WhatsApp Us
          </a>
          <a href="tel:9007184184" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full bg-[#1A4D45] text-[#FAF6EF] hover:bg-[#133c32] transition-all">
            <Phone size={14} /> Call 7AM–10PM
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="xl:hidden p-2 text-[#1A4D45]" aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#FAF6EF] border-b border-[#E8E2D2] px-6 py-4 space-y-2">
          {navLinks.map((l) => (
            <a key={l.name} href={l.href} onClick={() => setOpen(false)} className="block py-2 text-base font-medium text-[#4A4743] hover:text-[#1A4D45]">
              {l.name}
            </a>
          ))}
          <div className="pt-2 flex gap-2">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase rounded-xl bg-[#C89D3C] text-white">
              <MessageSquare size={16} /> WhatsApp
            </a>
            <a href="tel:9007184184" className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase rounded-xl bg-[#1A4D45] text-white">
              <Phone size={16} /> Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => (
  <footer className="bg-[#2D2A26] text-[#FAF6EF] border-t border-[#C89D3C]/30 pt-14 pb-8">
    <div className="max-w-6xl mx-auto px-6 lg:px-8 space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5 space-y-4">
          <div className="flex items-center space-x-3">
            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="Animal Analytics logo" className="w-9 h-9 object-contain" />
            </div>
            <span className="font-serif text-xl font-bold text-[#FAF6EF]">Animal Analytics</span>
          </div>
          <p className="text-xs sm:text-sm text-[#FAF6EF]/80 font-light max-w-sm leading-relaxed">
            Trusted animal &amp; pet pathology lab in Kolkata since 2012 — accurate dog &amp; pet blood tests, urine tests, stool tests and full diagnostics, with home sample collection across West Bengal.
          </p>
        </div>
        <div className="md:col-span-3 space-y-3">
          <h4 className="text-xs uppercase tracking-widest font-semibold text-[#C89D3C]">Explore</h4>
          <ul className="space-y-2 text-xs sm:text-sm text-[#FAF6EF]/85 font-light">
            <li><a href="/#services" className="hover:text-white transition">Services</a></li>
            <li><a href="/#animals" className="hover:text-white transition">Animals We Serve</a></li>
            <li><a href="/#reach" className="hover:text-white transition">Areas We Cover</a></li>
            <li><Link to="/blog" className="hover:text-white transition">Guides &amp; Blog</Link></li>
            <li><a href="/#faq" className="hover:text-white transition">FAQ</a></li>
          </ul>
        </div>
        <div className="md:col-span-4 space-y-3 text-xs sm:text-sm text-[#FAF6EF]/85 font-light">
          <h4 className="text-xs uppercase tracking-widest font-semibold text-[#C89D3C]">Contact</h4>
          <p><strong>Call:</strong> 9007184184</p>
          <p><strong>WhatsApp:</strong> 9748600483</p>
          <p><strong>Email:</strong> animal.analytics16@gmail.com</p>
          <p><strong>Hours:</strong> 7AM – 10PM, every day</p>
        </div>
      </div>
      <div className="border-t border-[#FAF6EF]/10 pt-6 text-[11px] text-[#FAF6EF]/50 flex flex-col sm:flex-row justify-between gap-2">
        <p>© 2026 Animal Analytics. All rights reserved.</p>
        <p>Animal &amp; Pet Pathology Lab serving Kolkata &amp; all of West Bengal.</p>
      </div>
    </div>
  </footer>
);

export { WA };
